import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Save, Webhook, Loader2, RefreshCw, Wrench,
  CheckCircle2, XCircle, AlertTriangle, ExternalLink, Copy, Info, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { http } from '@/lib/api';

// ── helpers ───────────────────────────────────────────────────────────────────

async function fetchDiagnostics() {
  const r = await http.get('/admin/stripe/webhook-diagnostics');
  return r.data;
}

async function fetchLastWebhook() {
  const r = await http.get('/admin/stripe/recent-events', { params: { limit: 1 } });
  return (r.data.events || [])[0] || null;
}

async function runFix() {
  const r = await http.post('/admin/stripe/fix-webhook', {});
  return r.data;
}

function fmtAge(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)  return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ ok, label }) {
  return (
    <span className={cn(
      'flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
      ok ? 'bg-emerald-500/15 text-emerald-700' : 'bg-rose-500/15 text-rose-600',
    )}>
      {ok
        ? <CheckCircle2 className="h-3 w-3" />
        : <XCircle     className="h-3 w-3" />}
      {label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function StripeWebhookConfig({ cfg, onSaved }) {
  const [secret,       setSecret]      = useState('');
  const [saving,       setSaving]      = useState(false);
  const [diag,         setDiag]        = useState(null);
  const [diagLoading,  setDiagLoading] = useState(false);
  const [fixing,       setFixing]      = useState(false);
  const [fixResult,    setFixResult]   = useState(null);
  const [lastWebhook,  setLastWebhook] = useState(undefined); // undefined = loading, null = none

  const configured = Boolean(cfg?.stripe_webhook_secret_configured);

  const loadDiag = useCallback(async () => {
    setDiagLoading(true);
    try {
      const [d, lw] = await Promise.all([fetchDiagnostics(), fetchLastWebhook()]);
      setDiag(d);
      setLastWebhook(lw);
    } catch (e) {
      toast.error('Could not load webhook diagnostics.');
    } finally {
      setDiagLoading(false);
    }
  }, []);

  // Load diagnostics on mount
  useEffect(() => { loadDiag(); }, [loadDiag]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.saveConfig({ stripe_webhook_secret: secret });
      toast.success(secret ? 'Stripe webhook secret saved.' : 'Stripe webhook secret cleared.');
      setSecret('');
      onSaved?.();
      loadDiag();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save webhook secret.');
    } finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await adminApi.saveConfig({ stripe_webhook_secret: '' });
      toast.success('Stripe webhook secret cleared.');
      setSecret('');
      onSaved?.();
      loadDiag();
    } catch { toast.error('Clear failed.'); }
    finally { setSaving(false); }
  };

  const handleFix = async () => {
    setFixing(true);
    setFixResult(null);
    try {
      const result = await runFix();
      setFixResult(result);
      if (result.ok && result.action === 'registered') {
        toast.success('Webhook auto-registered. Copy the signing secret below and save it.');
      } else if (result.ok) {
        toast.info(result.note || 'Fix complete.');
      }
      loadDiag();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Auto-fix failed.');
    } finally {
      setFixing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
  };

  const healthy = diag?.healthy;

  return (
    <div className="mt-4 rounded-md border border-border bg-card" data-testid="admin-stripe-webhook">

      {/* ── Header ── */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Webhook className="h-4 w-4 text-primary" />
          <span className="text-[13px] font-semibold">Stripe Webhook</span>
          {diag && <StatusBadge ok={healthy} label={healthy ? 'Healthy' : 'Needs attention'} />}
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm" variant="ghost"
            className="h-7 gap-1 px-2 text-[12px]"
            onClick={loadDiag}
            disabled={diagLoading}
            data-testid="stripe-webhook-refresh"
          >
            {diagLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </Button>
          <a
            href="https://dashboard.stripe.com/webhooks"
            target="_blank" rel="noopener noreferrer"
            className="flex h-7 items-center gap-1 rounded-md px-2 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            Stripe Dashboard
          </a>
        </div>
      </div>

      {/* ── Diagnostics panel ── */}
      {diagLoading && !diag ? (
        <div className="flex items-center gap-2 px-4 py-3 text-[12.5px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Running diagnostics…
        </div>
      ) : diag ? (
        <div className="px-4 py-3 space-y-3">

          {/* Emergent managed notice */}
          {diag.using_emergent_key ? (
            <div className="flex items-start gap-2 rounded-md bg-sky-50 border border-sky-200 px-3 py-2 text-[12.5px] text-sky-700">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Emergent managed Stripe key — webhook delivery is handled automatically. No configuration needed.</span>
            </div>
          ) : (
            <>
              {/* ── Last webhook received (read-only, no secret) ── */}
              <div
                className={cn(
                  'flex items-center gap-3 rounded-md border px-3 py-2 text-[12.5px]',
                  lastWebhook
                    ? lastWebhook.error
                      ? 'border-rose-200 bg-rose-50/40'
                      : 'border-emerald-200 bg-emerald-50/40'
                    : 'border-border bg-background',
                )}
                data-testid="admin-stripe-last-webhook"
              >
                <Clock className={cn(
                  'h-4 w-4 shrink-0',
                  lastWebhook
                    ? lastWebhook.error ? 'text-rose-500' : 'text-emerald-600'
                    : 'text-muted-foreground',
                )} />
                <div className="flex-1 min-w-0">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Last webhook received
                  </span>
                  {lastWebhook === undefined && (
                    <p className="mt-0.5 text-muted-foreground">Loading…</p>
                  )}
                  {lastWebhook === null && (
                    <p className="mt-0.5 text-muted-foreground">No events recorded yet.</p>
                  )}
                  {lastWebhook && (
                    <div className="mt-0.5 flex flex-wrap items-center gap-2">
                      <code className="rounded bg-muted px-1 text-[12px] font-mono">
                        {lastWebhook.type}
                      </code>
                      <span className="text-muted-foreground">
                        {fmtAge(lastWebhook.received_at)}
                      </span>
                      {lastWebhook.source && (
                        <span className="text-[11px] text-muted-foreground">
                          · {lastWebhook.source}
                        </span>
                      )}
                      {lastWebhook.error ? (
                        <span className="flex items-center gap-1 text-[11px] text-rose-600">
                          <XCircle className="h-3 w-3" /> processing error
                        </span>
                      ) : lastWebhook.processed ? (
                        <span className="flex items-center gap-1 text-[11px] text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> processed
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              {/* Status grid */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <div className="rounded-md border border-border bg-background p-2">
                  <div className="text-[11px] text-muted-foreground mb-1">Webhook endpoint</div>
                  <StatusBadge
                    ok={diag.registered_endpoints?.some(e => e.is_correct_url && e.status === 'enabled')}
                    label={diag.registered_endpoints?.some(e => e.is_correct_url && e.status === 'enabled')
                      ? 'Registered' : 'Not registered'}
                  />
                </div>
                <div className="rounded-md border border-border bg-background p-2">
                  <div className="text-[11px] text-muted-foreground mb-1">Signing secret</div>
                  <StatusBadge ok={diag.webhook_secret_configured} label={diag.webhook_secret_configured ? 'Set' : 'Missing'} />
                </div>
                <div className="rounded-md border border-border bg-background p-2">
                  <div className="text-[11px] text-muted-foreground mb-1">Overall status</div>
                  <StatusBadge ok={diag.healthy} label={diag.healthy ? 'Healthy' : 'Broken'} />
                </div>
              </div>

              {/* Canonical URL */}
              <div className="rounded-md border border-border bg-background p-2">
                <div className="text-[11px] text-muted-foreground mb-1">Correct webhook URL for this deployment</div>
                <div className="flex items-center gap-1.5">
                  <code className="text-[12px] text-foreground break-all">{diag.canonical_webhook_url}</code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(diag.canonical_webhook_url)}
                    className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                    title="Copy URL"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {/* Registered endpoints */}
              {diag.registered_endpoints?.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[11.5px] font-semibold text-foreground">Registered endpoints in Stripe</div>
                  {diag.registered_endpoints.map((ep, i) => (
                    <div key={ep.id || i} className={cn(
                      'flex items-start justify-between gap-2 rounded-md border px-2.5 py-1.5 text-[12px]',
                      ep.is_correct_url && ep.status === 'enabled'
                        ? 'border-emerald-200 bg-emerald-50/50'
                        : 'border-rose-200 bg-rose-50/50',
                    )}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          {ep.is_correct_url && ep.status === 'enabled'
                            ? <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />
                            : <AlertTriangle className="h-3 w-3 text-rose-500 shrink-0" />}
                          <span className={cn('break-all', ep.is_correct_url ? 'text-foreground' : 'text-rose-700')}>
                            {ep.url}
                          </span>
                        </div>
                        {!ep.is_correct_url && (
                          <div className="mt-0.5 ml-4.5 text-[11px] text-rose-600">
                            Stale — this URL is returning 404. Disable or delete it in the Stripe dashboard.
                          </div>
                        )}
                      </div>
                      <span className={cn(
                        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                        ep.status === 'enabled' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700',
                      )}>
                        {ep.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Action needed */}
              {diag.action_needed?.length > 0 && (
                <div className="space-y-1">
                  {diag.action_needed.map((action, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[12.5px] text-amber-700">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{action}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Auto-fix button */}
              {!healthy && (
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    size="sm"
                    className="h-7 gap-1.5 bg-amber-600 hover:bg-amber-700 text-[12px]"
                    onClick={handleFix}
                    disabled={fixing}
                    data-testid="stripe-webhook-fix-btn"
                  >
                    {fixing
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Wrench className="h-3.5 w-3.5" />}
                    {fixing ? 'Registering…' : 'Auto-register correct endpoint'}
                  </Button>
                  <span className="text-[11.5px] text-muted-foreground">
                    Creates a new webhook endpoint pointing to the correct URL and disables stale ones.
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      {/* ── Fix result ── */}
      {fixResult && (
        <div className={cn(
          'mx-4 mb-3 rounded-md border p-3 text-[12.5px] space-y-2',
          fixResult.action === 'registered'
            ? 'border-emerald-200 bg-emerald-50/50'
            : 'border-sky-200 bg-sky-50/50',
        )} data-testid="stripe-webhook-fix-result">
          <div className="font-semibold">
            {fixResult.action === 'registered' && '✓ New webhook endpoint registered'}
            {fixResult.action === 'already_registered' && 'ℹ Already correctly registered'}
            {fixResult.action === 'none' && 'ℹ No action needed'}
          </div>

          {fixResult.signing_secret && (
            <div className="rounded-md border border-emerald-300 bg-white p-2 space-y-1">
              <div className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">
                Signing secret — copy this now, it is shown once
              </div>
              <div className="flex items-center gap-1.5">
                <code className="flex-1 text-[12px] font-mono break-all text-foreground">
                  {fixResult.signing_secret}
                </code>
                <Button
                  size="sm" variant="outline"
                  className="h-6 gap-1 px-2 text-[11px] shrink-0"
                  onClick={() => copyToClipboard(fixResult.signing_secret)}
                >
                  <Copy className="h-3 w-3" /> Copy
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Save this to <strong>Deployment Panel → Secrets → STRIPE_WEBHOOK_SECRET (live slot)</strong>, then redeploy.
              </div>
            </div>
          )}

          {fixResult.note && (
            <div className="text-muted-foreground">{fixResult.note}</div>
          )}
          {fixResult.next_step && (
            <div className="text-sky-700">{fixResult.next_step}</div>
          )}
          {fixResult.disabled_stale_count > 0 && (
            <div className="text-[11.5px] text-amber-700">
              {fixResult.disabled_stale_count} stale endpoint(s) disabled. You can safely delete them from the Stripe dashboard.
            </div>
          )}
        </div>
      )}

      {/* ── Manual secret entry ── */}
      <div className="border-t border-border px-4 py-3">
        <div className="text-[12px] text-muted-foreground mb-2">
          Enter the <code>whsec_…</code> signing secret from{' '}
          <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer"
             className="text-primary hover:underline">dashboard.stripe.com/webhooks</a>
          {' '}to verify webhook signatures.{' '}
          {configured
            ? <span className="text-emerald-700 font-medium">Currently configured.</span>
            : <span className="text-amber-600 font-medium">Not configured — events will be rejected (503).</span>}
        </div>
        <div className="flex gap-2">
          <div className="flex-1 max-w-md">
            <Input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={configured ? 'whsec_… (replace current)' : 'whsec_xxxxxxxxxxxxx'}
              className="h-8 font-mono-stat text-[11.5px]"
              data-testid="admin-stripe-webhook-input"
            />
          </div>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || !secret}
            className="h-8 gap-1.5"
            data-testid="admin-stripe-webhook-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </Button>
          {configured && (
            <Button
              size="sm" variant="ghost"
              onClick={clear}
              disabled={saving}
              className="h-8 gap-1.5 text-muted-foreground"
              data-testid="admin-stripe-webhook-clear"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
